type ScopeType = 'file' | 'global';

type SupabaseLike = {
  from: (table: string) => any;
  rpc: (fn: string, args: Record<string, any>) => Promise<{ data: any; error: any }>;
};

type AnnotationUpsertOp = {
  action: 'upsert';
  annotation_uid: string;
  page_number: number;
  component_type: string;
  annotation_type: string;
  geometry: Record<string, any>;
  style: Record<string, any>;
  meta: Record<string, any>;
};

type AnnotationDeleteOp = {
  action: 'hard_delete';
  annotation_uid: string;
};

type AnnotationOp = AnnotationUpsertOp | AnnotationDeleteOp;

type AnnotationEntityRow = {
  annotation_uid: string;
  component_type: string;
  page_number: number;
  annotation_type: string;
  geometry: Record<string, any> | null;
  style: Record<string, any> | null;
  meta: Record<string, any> | null;
};

const OPS_CHUNK_SIZE = 100;

const chunkArray = <T,>(items: T[], chunkSize: number): T[][] => {
  if (chunkSize <= 0) return [items];
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
};

const GEOMETRY_KEYS = [
  'x', 'y', 'startX', 'startY', 'endX', 'endY',
  'width', 'height', 'radius',
  'relativeX', 'relativeY', 'relativeStartX', 'relativeStartY',
  'relativeWidth', 'relativeHeight',
  'savedCanvasWidth', 'savedCanvasHeight',
  'points',
  'startAnnotationId', 'endAnnotationId',
  'measurement', 'measurementUnit',
  'totalLength',
] as const;

const STYLE_KEYS = [
  'color', 'thickness', 'text', 'number',
  'wireTypeColor', 'wireTypeName',
] as const;

const RESERVED_META_KEYS = new Set<string>([
  'id', 'page', 'component_type', 'type', ...GEOMETRY_KEYS, ...STYLE_KEYS,
]);

const toScope = (activeMode: string, currentDrawingFileId?: string | null): { scopeType: ScopeType; fileId: string | null } => {
  if (activeMode === 'draw' && currentDrawingFileId) {
    return { scopeType: 'file', fileId: currentDrawingFileId };
  }
  return { scopeType: 'global', fileId: null };
};

const buildScopedQuery = (query: any, scopeType: ScopeType, fileId: string | null) => {
  const withScope = query.eq('scope_type', scopeType);
  if (scopeType === 'file' && fileId) return withScope.eq('file_id', fileId);
  return withScope.is('file_id', null);
};

const buildCompatScopedQuery = (query: any, fileId: string | null) => {
  if (fileId) return query.eq('file_id', fileId);
  return query.is('file_id', null);
};

const pick = (source: Record<string, any>, keys: readonly string[]) => {
  const out: Record<string, any> = {};
  for (const key of keys) {
    if (source[key] !== undefined) out[key] = source[key];
  }
  return out;
};

const buildMeta = (annotation: Record<string, any>) => {
  const meta: Record<string, any> = {};
  for (const [key, value] of Object.entries(annotation)) {
    if (!RESERVED_META_KEYS.has(key) && value !== undefined) {
      meta[key] = value;
    }
  }
  return meta;
};

const buildOpId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `op-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const getScopeVersion = async (
  supabase: SupabaseLike,
  estimationId: string,
  scopeType: ScopeType,
  fileId: string | null
): Promise<number> => {
  let query = supabase
    .from('annotation_scope_versions')
    .select('version')
    .eq('estimation_id', estimationId)
    .eq('scope_type', scopeType);
  query = scopeType === 'file' && fileId ? query.eq('file_id', fileId) : query.is('file_id', null);
  const { data, error } = await query.maybeSingle();
  if (error) return 0;
  return data?.version ?? 0;
};

const buildUpsertOp = (annotation: Record<string, any>): AnnotationUpsertOp => {
  const fallbackId = `ann-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return {
    action: 'upsert',
    annotation_uid: String(annotation.id || fallbackId),
    page_number: Number(annotation.page || 1),
    component_type: String(annotation.component_type || 'unknown'),
    annotation_type: String(annotation.type || 'unknown'),
    geometry: pick(annotation, GEOMETRY_KEYS),
    style: pick(annotation, STYLE_KEYS),
    meta: buildMeta(annotation),
  };
};

const applyAnnotationOpsWithRetry = async (params: {
  supabase: SupabaseLike;
  estimationId: string;
  scopeType: ScopeType;
  fileId: string | null;
  ops: AnnotationOp[];
}): Promise<void> => {
  const { supabase, estimationId, scopeType, fileId, ops } = params;

  if (!ops.length) return;

  const invoke = async (expectedVersion: number) =>
    supabase.rpc('apply_annotation_ops_v2', {
      p_estimation_id: estimationId,
      p_scope_type: scopeType,
      p_file_id: fileId,
      p_expected_version: expectedVersion,
      p_op_id: buildOpId(),
      p_ops: ops,
    });

  let expectedVersion = await getScopeVersion(supabase, estimationId, scopeType, fileId);
  let attempts = 0;

  while (attempts < 2) {
    attempts += 1;
    const { data, error } = await invoke(expectedVersion);
    if (error) throw error;

    const first = Array.isArray(data) ? data[0] : data;
    if (!first?.conflict) return;

    expectedVersion = await getScopeVersion(supabase, estimationId, scopeType, fileId);
  }

  throw new Error('Failed to apply annotation ops due to repeated version conflicts');
};

export async function upsertAnnotationsV2(params: {
  supabase: SupabaseLike;
  estimationId: string;
  activeMode: string;
  currentDrawingFileId?: string | null;
  annotations: Array<Record<string, any>>;
}): Promise<{ changedComponentTypes: string[] }> {
  const {
    supabase,
    estimationId,
    activeMode,
    currentDrawingFileId,
    annotations,
  } = params;

  const { scopeType, fileId } = toScope(activeMode, currentDrawingFileId);
  const snapshot = Array.isArray(annotations) ? annotations : [];
  const upsertOps = snapshot.map(buildUpsertOp);

  if (!upsertOps.length) {
    return { changedComponentTypes: [] };
  }

  // IMPORTANT:
  // Avoid large `.in(annotation_uid)` lookups and massive single-RPC payloads.
  // For reliability under long sessions / large projects, send bounded op chunks.
  const opChunks = chunkArray(upsertOps, OPS_CHUNK_SIZE);
  for (const ops of opChunks) {
    await applyAnnotationOpsWithRetry({
      supabase,
      estimationId,
      scopeType,
      fileId,
      ops,
    });
  }

  const changedComponentTypes = Array.from(
    new Set(upsertOps.map((op) => op.component_type).filter(Boolean))
  );
  return { changedComponentTypes };
}

export async function deleteAnnotationsByIdV2(params: {
  supabase: SupabaseLike;
  estimationId: string;
  activeMode: string;
  currentDrawingFileId?: string | null;
  annotationIds: string[];
  knownComponentTypes?: string[];
}): Promise<{ changedComponentTypes: string[] }> {
  const {
    supabase,
    estimationId,
    activeMode,
    currentDrawingFileId,
    annotationIds,
    knownComponentTypes = [],
  } = params;

  const { scopeType, fileId } = toScope(activeMode, currentDrawingFileId);
  const uniqueIds = Array.from(new Set((annotationIds || []).map((id) => String(id)).filter(Boolean)));
  if (!uniqueIds.length) {
    return { changedComponentTypes: Array.from(new Set(knownComponentTypes.filter(Boolean))) };
  }

  const changedTypes = new Set<string>(knownComponentTypes.filter(Boolean));

  // If callers don't know types (or only partially know them), resolve from DB before delete.
  let query = supabase
    .from('annotation_entities_v2')
    .select('annotation_uid, component_type')
    .eq('estimation_id', estimationId)
    .is('deleted_at', null)
    .in('annotation_uid', uniqueIds);
  query = buildScopedQuery(query, scopeType, fileId);
  const { data: existingRows, error: existingError } = await query;
  if (existingError) throw existingError;
  if (Array.isArray(existingRows)) {
    for (const row of existingRows) {
      if (row?.component_type) changedTypes.add(String(row.component_type));
    }
  }

  const deleteOps: AnnotationDeleteOp[] = uniqueIds.map((annotationId) => ({
    action: 'hard_delete',
    annotation_uid: annotationId,
  }));

  const deleteChunks = chunkArray(deleteOps, OPS_CHUNK_SIZE);
  for (const ops of deleteChunks) {
    await applyAnnotationOpsWithRetry({
      supabase,
      estimationId,
      scopeType,
      fileId,
      ops,
    });
  }

  return { changedComponentTypes: Array.from(changedTypes) };
}

export async function saveAnnotationSnapshotV2(params: {
  supabase: SupabaseLike;
  estimationId: string;
  activeMode: string;
  currentDrawingFileId?: string | null;
  annotations: Array<Record<string, any>>;
  allowPrune?: boolean;
}): Promise<{ changedComponentTypes: string[] }> {
  const {
    supabase,
    estimationId,
    activeMode,
    currentDrawingFileId,
    annotations,
  } = params;
  // Intentionally ignored: prune-by-missing causes stale-snapshot races.
  // Deletes must be explicit hard_delete ops via deleteAnnotationsByIdV2.
  void params.allowPrune;
  return upsertAnnotationsV2({
    supabase,
    estimationId,
    activeMode,
    currentDrawingFileId,
    annotations,
  });
}

const toLegacyAnnotation = (row: AnnotationEntityRow): Record<string, any> => {
  const geometry = row.geometry && typeof row.geometry === 'object' ? row.geometry : {};
  const style = row.style && typeof row.style === 'object' ? row.style : {};
  const meta = row.meta && typeof row.meta === 'object' ? row.meta : {};
  return {
    ...meta,
    ...geometry,
    ...style,
    id: row.annotation_uid,
    page: row.page_number,
    component_type: row.component_type,
    type: row.annotation_type,
  };
};

const groupRowsByComponent = (rows: AnnotationEntityRow[]) => {
  const grouped = new Map<string, Record<string, any>[]>();
  for (const row of rows) {
    const componentType = String(row.component_type || 'unknown');
    if (!grouped.has(componentType)) grouped.set(componentType, []);
    grouped.get(componentType)!.push(toLegacyAnnotation(row));
  }
  return Array.from(grouped.entries()).map(([component_type, annotations]) => ({
    component_type,
    annotations,
  }));
};

const SELECT_PAGE_SIZE = 1000;

const fetchAllRowsPaged = async <T>(buildQuery: () => any): Promise<{ data: T[]; error: any }> => {
  const rows: T[] = [];
  let from = 0;

  while (true) {
    const to = from + SELECT_PAGE_SIZE - 1;
    const { data, error } = await buildQuery().range(from, to);
    if (error) {
      return { data: [], error };
    }

    const batch = (data || []) as T[];
    rows.push(...batch);

    if (batch.length < SELECT_PAGE_SIZE) {
      break;
    }

    from += SELECT_PAGE_SIZE;
  }

  return { data: rows, error: null };
};

export async function fetchAnnotationCountMapsV2(params: {
  supabase: SupabaseLike;
  estimationId: string;
  componentTypes?: string[];
}): Promise<Map<string, Map<number, number>>> {
  const { supabase, estimationId, componentTypes } = params;
  const normalizedTypes = Array.from(new Set((componentTypes || []).filter(Boolean)));

  // Use the high-performance counting view (Ctrl+F style)
  let query = supabase
    .from('annotation_counts_v2')
    .select('component_type, page_number, count')
    .eq('estimation_id', estimationId);

  if (normalizedTypes.length > 0) {
    query = query.in('component_type', normalizedTypes);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Error fetching annotation counts from view:', error);
    // Fallback to manual count if view doesn't exist yet or fails
    return fetchAnnotationCountMapsV2Legacy(params);
  }

  const counts = new Map<string, Map<number, number>>();
  for (const row of (data || [])) {
    const componentType = String(row.component_type || '');
    const page = Number(row.page_number || 0);
    const count = Number(row.count || 0);
    if (!componentType || !page) continue;

    if (!counts.has(componentType)) counts.set(componentType, new Map<number, number>());
    counts.get(componentType)!.set(page, count);
  }

  return counts;
}

// Legacy fallback for environments where the view hasn't been deployed yet
async function fetchAnnotationCountMapsV2Legacy(params: {
  supabase: SupabaseLike;
  estimationId: string;
  componentTypes?: string[];
}): Promise<Map<string, Map<number, number>>> {
  const { supabase, estimationId, componentTypes } = params;
  const normalizedTypes = Array.from(new Set((componentTypes || []).filter(Boolean)));

  let query = supabase
    .from('annotation_entities_v2')
    .select('component_type, page_number')
    .eq('estimation_id', estimationId)
    .is('deleted_at', null);

  if (normalizedTypes.length > 0) {
    query = query.in('component_type', normalizedTypes);
  }

  const { data, error } = await query;
  if (error) throw error;

  const counts = new Map<string, Map<number, number>>();
  for (const row of (data || [])) {
    const componentType = String(row.component_type || '');
    const page = Number(row.page_number || 0);
    if (!componentType || !page) continue;

    if (!counts.has(componentType)) counts.set(componentType, new Map<number, number>());
    const current = counts.get(componentType)!.get(page) || 0;
    counts.get(componentType)!.set(page, current + 1);
  }

  return counts;
}

export async function fetchComponentTotalsV2(params: {
  supabase: SupabaseLike;
  estimationId: string;
  componentTypes?: string[];
}): Promise<Map<string, number>> {
  const pageMap = await fetchAnnotationCountMapsV2(params);
  const totals = new Map<string, number>();
  pageMap.forEach((pageCounts, componentType) => {
    let total = 0;
    pageCounts.forEach((count) => {
      total += count;
    });
    totals.set(componentType, total);
  });
  return totals;
}

export async function loadGroupedAnnotationsFromV2(params: {
  supabase: SupabaseLike;
  estimationId: string;
  activeMode: string;
  currentDrawingFileId?: string | null;
  pageNumber?: number | null;
}) {
  const { supabase, estimationId, activeMode, currentDrawingFileId, pageNumber } = params;
  const normalizedPageNumber = typeof pageNumber === 'number' && Number.isFinite(pageNumber)
    ? Math.max(1, Math.floor(pageNumber))
    : null;

  if (activeMode === 'draw' && currentDrawingFileId) {
    const { data: fileRows, error: fileError } = await fetchAllRowsPaged<AnnotationEntityRow>(
      () => buildCompatScopedQuery(
        supabase
          .from('annotation_entities_v2')
          .select('annotation_uid, component_type, page_number, annotation_type, geometry, style, meta, file_id')
          .eq('estimation_id', estimationId)
          .eq('scope_type', 'file')
          .eq('page_number', normalizedPageNumber ?? 1)
          .is('deleted_at', null),
        currentDrawingFileId
      )
    );

    const { data: globalRows, error: globalError } = await fetchAllRowsPaged<AnnotationEntityRow>(
      () => buildCompatScopedQuery(
        supabase
          .from('annotation_entities_v2')
          .select('annotation_uid, component_type, page_number, annotation_type, geometry, style, meta, file_id')
          .eq('estimation_id', estimationId)
          .eq('scope_type', 'global')
          .eq('page_number', normalizedPageNumber ?? 1)
          .is('deleted_at', null),
        null
      )
    );

    const error = fileError || globalError;
    return {
      data: groupRowsByComponent([...(fileRows || []), ...(globalRows || [])] as AnnotationEntityRow[]),
      error,
    };
  }

  if (activeMode === 'draw') {
    return { data: null, error: null };
  }

  const { data, error } = await fetchAllRowsPaged<AnnotationEntityRow>(() =>
    supabase
      .from('annotation_entities_v2')
      .select('annotation_uid, component_type, page_number, annotation_type, geometry, style, meta, file_id, scope_type')
      .eq('estimation_id', estimationId)
      .is('deleted_at', null)
  );

  return {
    data: groupRowsByComponent((data || []) as AnnotationEntityRow[]),
    error,
  };
}
